import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaMobileAlt, FaSave, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend.cabzigo.com/api';

const AppVersion = () => {
  const [versions, setVersions] = useState({
    customer: {
      androidVersion: '2.1.0',
      iosVersion: '1.5.0'
    },
    driver: {
      androidVersion: '2.1.0',
      iosVersion: '2.5.0'
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch current versions
  useEffect(() => {
    fetchVersions();
  }, []);

  const fetchVersions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/app-version/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const data = response.data.data;
        const newVersions = { ...versions };
        
        data.forEach(item => {
          newVersions[item.type] = {
            androidVersion: item.android_version,
            iosVersion: item.ios_version
          };
        });
        
        setVersions(newVersions);
      }
    } catch (error) {
      console.error('Error fetching versions:', error);
      toast.error('Failed to fetch app versions');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (type, field, value) => {
    setVersions(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value
      }
    }));
  };

  const handleSave = async (type) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const data = versions[type];

      const response = await axios.put(`${API_URL}/app-version`, {
        type,
        androidVersion: data.androidVersion,
        iosVersion: data.iosVersion
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} app version updated successfully!`);
      }
    } catch (error) {
      console.error('Error saving version:', error);
      toast.error('Failed to update app version');
    } finally {
      setSaving(false);
    }
  };

  const VersionCard = ({ type, title }) => {
    const data = versions[type];
    const isCustomer = type === 'customer';

    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center mb-4">
          <FaMobileAlt className={`text-2xl mr-3 ${isCustomer ? 'text-blue-600' : 'text-green-600'}`} />
          <h2 className="text-xl font-bold text-gray-800">{title}</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Android Version
            </label>
            <input
              type="text"
              value={data.androidVersion}
              onChange={(e) => handleChange(type, 'androidVersion', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., 2.1.0"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              iOS Version
            </label>
            <input
              type="text"
              value={data.iosVersion}
              onChange={(e) => handleChange(type, 'iosVersion', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., 1.5.0"
            />
          </div>
        </div>

        {/* Save Button */}
        <button
          onClick={() => handleSave(type)}
          disabled={saving}
          className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <FaSpinner className="animate-spin mr-2" />
              Saving...
            </>
          ) : (
            <>
              <FaSave className="mr-2" />
              Save {title} Settings
            </>
          )}
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <FaSpinner className="animate-spin text-4xl text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">App Version Management</h1>
        <p className="text-gray-600">
          Manage mobile app versions for Customer and Driver apps.
        </p>
      </div>

      <VersionCard type="customer" title="Customer App" />
      <VersionCard type="driver" title="Driver App" />

      <div className="bg-gray-50 rounded-lg p-6 mt-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">API Response Preview</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-4 rounded-md shadow-sm">
            <h4 className="font-medium text-gray-700 mb-2">Customer App</h4>
            <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">
              {JSON.stringify({
                status: "true",
                type: "customer",
                android_version: versions.customer.androidVersion,
                ios_version: versions.customer.iosVersion
              }, null, 2)}
            </pre>
          </div>
          <div className="bg-white p-4 rounded-md shadow-sm">
            <h4 className="font-medium text-gray-700 mb-2">Driver App</h4>
            <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">
              {JSON.stringify({
                status: "true",
                type: "driver",
                android_version: versions.driver.androidVersion,
                ios_version: versions.driver.iosVersion
              }, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppVersion;
